import React, { useState } from "react";
import {
  FileBarChart2, Download, Loader2, HeartPulse, Bird, HardHat, Wallet,
  Boxes, Warehouse, Sprout, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { API_BASE, getToken } from "@/lib/api";
import { PageHero, Section, Button, Badge } from "@/components/premium/UI";

const REPORTS = [
  { key: "executive", label: "Executive summary",  description: "One-page homestead brief",         icon: TrendingUp,    accent: "primary" },
  { key: "poultry",   label: "Poultry360",          description: "Batches, sales, feed & mortality", icon: Bird,          accent: "accent" },
  { key: "health",    label: "Health360",           description: "Patient readings & compliance",    icon: HeartPulse,    accent: "success" },
  { key: "projects",  label: "Projects360",         description: "Construction progress & approvals",icon: HardHat,       accent: "accent" },
  { key: "finance",   label: "Finance360",          description: "Income, expense & cash position",  icon: Wallet,        accent: "primary" },
  { key: "inventory", label: "Inventory360",        description: "Stock levels, suppliers, low-stock",icon: Boxes,        accent: "primary" },
  { key: "assets",    label: "Assets360",           description: "Complete asset register",          icon: Warehouse,     accent: "primary" },
  { key: "farm",      label: "Farm360",             description: "Livestock & crops",                icon: Sprout,        accent: "success" },
];

const PERIODS = [
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "quarterly", label: "Quarterly" },
  { key: "annual", label: "Annual" },
];

const ACCENT_CLS = {
  primary: "bg-primary/10 text-primary",
  accent:  "bg-accent/10 text-accent",
  success: "bg-success/10 text-success",
};

export default function Reports() {
  const [busy, setBusy] = useState(null);
  const [period, setPeriod] = useState("monthly");

  async function download(key) {
    setBusy(key);
    try {
      const resp = await fetch(`${API_BASE}/reports/${key}?period=${period}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error(`Report failed (${resp.status})`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `home360-${key}-${period}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${key.charAt(0).toUpperCase() + key.slice(1)} report downloaded`);
    } catch {
      toast.error("Could not generate report. Please try again.");
    } finally { setBusy(null); }
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <PageHero
        eyebrow="Executive reporting"
        title="Reports"
        subtitle="Presentation-grade PDF reports for every domain. Delivered on demand."
        moduleKind="reports"
        actions={
          <div className="flex items-center gap-1.5 rounded-full bg-muted/60 p-1 border border-border" data-testid="reports-period-switch">
            {PERIODS.map(p => (
              <button
                key={p.key}
                data-testid={`reports-period-${p.key}`}
                onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${period === p.key ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        }
      />

      <Section
        title="Available reports"
        description={`${REPORTS.length} report generators · currently generating ${period} period`}
        actions={<Badge variant="primary">{period}</Badge>}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="reports-grid">
          {REPORTS.map(r => {
            const Icon = r.icon;
            const isBusy = busy === r.key;
            return (
              <div
                key={r.key}
                data-testid={`report-card-${r.key}`}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 flex flex-col transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-16px_hsl(var(--primary)/0.28)]"
              >
                <div className={`h-11 w-11 rounded-xl grid place-items-center mb-4 ${ACCENT_CLS[r.accent] || ACCENT_CLS.primary}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="font-heading text-base font-semibold tracking-tight">{r.label}</div>
                <div className="subtle mt-1 mb-4 line-clamp-2 min-h-[36px]">{r.description}</div>
                <Button
                  onClick={() => download(r.key)}
                  disabled={isBusy}
                  size="sm"
                  className="mt-auto w-full"
                  data-testid={`report-download-${r.key}`}
                >
                  {isBusy ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Preparing PDF…</>
                  ) : (
                    <><Download className="h-4 w-4" /> Download PDF</>
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="About Home360 reports" description="How the reporting engine works">
        <ul className="text-sm space-y-2 text-foreground/80 list-disc pl-5">
          <li>Reports are generated live from your authorised data — nothing is cached.</li>
          <li>You will only see reports for modules your role can access. Data outside your scope is excluded automatically.</li>
          <li>Periods filter the data window (weekly / monthly / quarterly / annual). The executive summary always covers the current month plus a rolling 12-month view.</li>
          <li>Home360 never diagnoses or advises clinically. Health reports present readings only.</li>
        </ul>
      </Section>
    </div>
  );
}
